import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import ForumOutlined from '@mui/icons-material/ForumOutlined'
import { Box, Skeleton, Stack, Typography } from '@mui/material'
import { useStytchMember } from '@stytch/react/b2b'
import { Member } from '@stytch/vanilla-js'
import { QueryKey } from '@tanstack/react-query'
import useOverlay from '@/hooks/useOverlay'
import useUsersById from '@/hooks/useUsersById'
import { useDeleteDocumentComment } from '@/service-library/hooks/document-comments'
import { DocComment } from '@/types/comments'
import { showErrorSnackbar } from '@/utils/snackbars'
import CommentDeleteDialog from './CommentDeleteDialog'
import CommentListItem from './CommentListItem'

type CommentListProps = {
  comments: DocComment[]
  isLoading?: boolean
  /** The cached list query this panel reads from, so a delete can rewrite the matching item to a tombstone in place. */
  listQueryKey?: QueryKey
  /** The count badge's query key, invalidated on delete so the badge count stays
   * in sync with the list. */
  countQueryKey?: QueryKey
}

function LoadingSkeleton() {
  return (
    <Stack spacing={2.5} sx={{ p: 2 }}>
      {[0, 1, 2].map((key) => (
        <Stack key={key} direction="row" spacing={1.5}>
          <Skeleton variant="circular" width={32} height={32} />
          <Stack sx={{ flex: 1 }} spacing={0.75}>
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="text" width="90%" />
            <Skeleton variant="text" width="70%" />
          </Stack>
        </Stack>
      ))}
    </Stack>
  )
}

function EmptyState() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      spacing={1.5}
      sx={{ minHeight: '100%', textAlign: 'center', px: 4, py: 6 }}
    >
      <ForumOutlined sx={{ fontSize: 48, color: 'text.disabled' }} />
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        No comments yet
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Be the first to leave a comment on this document.
      </Typography>
    </Stack>
  )
}

export default function CommentList({
  comments,
  isLoading = false,
  listQueryKey,
  countQueryKey,
}: CommentListProps) {
  const { member } = useStytchMember()
  const stytchMember = member as Member
  const userId = stytchMember.external_id as string
  const { usersById, isPending: authorsLoading } = useUsersById({
    userIds: comments
      .map((comment) => comment.created_by_user_id)
      .filter((authorId) => authorId !== userId),
  })
  const usersWithCurrentUserId = {
    // The current user's name comes from Stytch instead of the author lookup.
    // Looking it up would put the list in a loading state the first time they
    // post a comment.
    [userId]: { id: userId, name: stytchMember.name },
    ...Object.fromEntries(usersById),
  }

  const [hasLoaded, setHasLoaded] = useState(false)
  const isReady = hasLoaded || (!isLoading && !authorsLoading)
  useEffect(() => {
    if (isReady) setHasLoaded(true)
  }, [isReady])
  const lastComment = comments[comments.length - 1]
  const lastCommentId = lastComment?.id ?? null
  const lastCommentIsOwn = lastComment?.created_by_user_id === userId
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const hasScrolledOnLoadRef = useRef(false)
  const previousLastCommentIdRef = useRef<string | null>(null)

  // The list opens scrolled to the newest comment. When the user posts a
  // comment, the list scrolls down to show it. It does not scroll for a comment
  // someone else added, because that would pull the user away from what they
  // are reading.
  useLayoutEffect(() => {
    if (!isReady) return
    const previousLastCommentId = previousLastCommentIdRef.current
    previousLastCommentIdRef.current = lastCommentId
    const container = scrollContainerRef.current
    if (!container) return

    if (!hasScrolledOnLoadRef.current) {
      hasScrolledOnLoadRef.current = true
      container.scrollTop = container.scrollHeight
    } else if (lastCommentId !== previousLastCommentId && lastCommentIsOwn) {
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' })
    }
  }, [isReady, lastCommentId, lastCommentIsOwn])

  const deleteOverlay = useOverlay()
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)

  const { deleteDocumentComment } = useDeleteDocumentComment({
    listQueryKey,
    sideEffectQueryKeys: [countQueryKey],
    onError: () => {
      showErrorSnackbar('Failed to delete comment. Please try again.')
    },
  })

  const requestDelete = (id: string) => {
    setPendingDeleteId(id)
    deleteOverlay.open()
  }

  const confirmDelete = () => {
    if (pendingDeleteId) deleteDocumentComment(pendingDeleteId)
    deleteOverlay.close()
  }

  // On the first load, show the skeleton until the comments and every author
  // have resolved, so names appear all at once. After that, a new author's name
  // loads in place, so the list stays on screen and keeps its scroll position.
  let content
  if (!isReady) {
    content = <LoadingSkeleton />
  } else if (comments.length === 0) {
    content = <EmptyState />
  } else {
    content = (
      <>
        <Stack sx={{ py: 1 }}>
          {comments.map((comment) => (
            <CommentListItem
              key={comment.id}
              comment={comment}
              authorName={
                usersWithCurrentUserId[comment.created_by_user_id]?.name
              }
              authorLoading={
                authorsLoading &&
                !usersWithCurrentUserId[comment.created_by_user_id]
              }
              isOwn={!!userId && comment.created_by_user_id === userId}
              onDeleteClick={() => requestDelete(comment.id)}
            />
          ))}
        </Stack>
        <CommentDeleteDialog
          overlay={deleteOverlay}
          onClose={deleteOverlay.close}
          onConfirm={confirmDelete}
        />
      </>
    )
  }

  return (
    <Box
      ref={scrollContainerRef}
      data-testid="comment-list-scroll-container"
      sx={{ flex: 1, minHeight: 120, overflowY: 'auto' }}
    >
      {content}
    </Box>
  )
}
